//Imports
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select, { components } from "react-select";
import { db, storage } from "../../firebase";
import { doc, updateDoc, Timestamp, arrayUnion, arrayRemove, runTransaction, collection } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
//Imports Context
import { AuthContext } from "../../contexts/AuthContext";
import { AppContext } from "../../contexts/AppContext";
import { DepartmentContext } from "../../contexts/DepartmentContext";
import staticData from '../../contexts/staticData.json';
//Imports Helpers
import { formatCurrency, formatDate, formatDisplayName, getSubventionNames } from "../../shared/helpers";
//Imports Icons
import downArrow from '../../assets/icons/downArrowWhite.svg';
import upload from '../../assets/icons/upload.svg';
import send from '../../assets/icons/send.svg';
import historyIcon from '../../assets/icons/history.svg';
import info from '../../assets/icons/info.svg';
//Import CSS
import './viewRequest.css';

function ViewPayment({ request, setSelectedRequest, onRequestResponse, requestObserved, answerObservation, data, departmentName, currentRequestType, view, viewObserved, viewResponseObservation }) {
    const navigate = useNavigate();
    const filteredDepartments = staticData.departmentsSent.filter(dept => dept !== departmentName);
    // Contexts
    const { userData } = useContext(AuthContext);
    const { establishmentList, pmeList } = useContext(AppContext);
    const { responseRequest, setResponseRequest } = useContext(DepartmentContext);
    //States
    // const [responseRequest, setResponseRequest] = useState({
    //     responseRequest: null,
    //     sendTo: null,
    //     comments: '',
    //     backups: departmentName === 'Departamento Tesorería' ? null : []
    // });
    const [showAdditionalFields, setShowAdditionalFields] = useState(false);
    const [showPaymentProofWarning, setShowPaymentProofWarning] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    //Effects
    useEffect(() => {
        if (departmentName) {
            setResponseRequest(prevState => ({
                ...prevState,
                backups: departmentName === 'Departamento Tesorería' ? null : []
            }));
        };
    }, [departmentName]);

    useEffect(() => {
        if (responseRequest.responseRequest || requestObserved) {
            setShowAdditionalFields(true);
            if (responseRequest.responseRequest?.value === 'rejected') {
                setResponseRequest(prevState => ({
                    ...prevState,
                    sendTo: { value: 'Establecimiento', label: 'Establecimiento' }
                }));
            }
        } else {
            setShowAdditionalFields(false);
        }
    }, [responseRequest.responseRequest, requestObserved]);

    //Helper Functions
    const getEstablishment = () => {
        return establishmentList.find(establishment => establishment.id === request.request?.idEstablishment);
    };

    const isFormValid = () => {
        //Validación común para todos los casos
        const baseValidation = responseRequest.responseRequest &&
            responseRequest.comments.trim() !== '' &&
            (responseRequest.responseRequest.value === 'rejected' || responseRequest.sendTo);

        // Caso base para solicitud observada
        if (requestObserved) {
            return responseRequest.comments.trim() !== '';
        }

        // Validación específica para tesorería
        if (departmentName === 'Departamento Tesorería') {
            //Si se envia directamente a establecimiento como aprobado, requiere comprobante de pago
            if (responseRequest.responseRequest?.value === 'approved' &&
                responseRequest.sendTo?.value === 'Establecimiento') {
                return baseValidation &&
                    responseRequest.backups &&
                    responseRequest.backups.length > 0;
            }
            //Si se envía a otro departamento, solo requiere comentarios y destinatario 
            return baseValidation;
        }

        // Para los demás casos solo validación base
        return baseValidation;
    };

    const getObservationDetails = () => {
        if (!request.request.history || request.request.history.length === 0) return null;

        const lastHistoryEntry = request.request.history[request.request.history.length - 1];

        if (lastHistoryEntry.responseRequest === 'observed') {
            return {
                action: lastHistoryEntry.action,
                observedBy: formatDisplayName(lastHistoryEntry.responsibleName),
                role: lastHistoryEntry.role,
                details: lastHistoryEntry.details,
                timestamp: formatDate(lastHistoryEntry.timestamp),
                backups: lastHistoryEntry.backups || [],
                response: lastHistoryEntry.response
            };
        }
        return baseValidation && responseRequest.backups.paymentProof !== null;
    };

    //Events Handlers
    const handleChangeSelect = (selectedOption, { name }) => {
        setResponseRequest(prevState => ({
            ...prevState,
            [name]: selectedOption
        }))
    };

    const handleChange = (e) => {
        const { name, files, value } = e.target;

        if (name === 'backups') {
            // Convertir FileList a array y agregar a los existentes
            const newFiles = Array.from(files);
            setResponseRequest(prevState => ({
                ...prevState,
                backups: prevState.backups
                    ? [...prevState.backups, ...newFiles]  // Agregar a archivos existentes
                    : newFiles  // O iniciar con nuevos archivos
            }));

            // Limpiar el input de archivo
            e.target.value = null;
        } else {
            setResponseRequest(prevState => ({
                ...prevState,
                [name]: value
            }));
        }
    };

    const handleHistoryClick = () => {
        const establishment = getEstablishment()?.nameEstablishment;
        const currentState = {
            previousPath: location.pathname,
            selectedRequest: request,
            data: data,
            requestObserved,
            answerObservation,
            currentRequestType,
            view,
            viewObserved,
            viewResponseObservation
        };

        navigate(`/establecimiento/seguimiento-pago/${request.request.id}`, {
            state: {
                establishment,
                returnState: currentState
            }
        });
    };

    const handleOriginalRequestHistoryClick = () => {
        const establishment = getEstablishment()?.nameEstablishment;
        const currentState = {
            previousPath: location.pathname,
            selectedRequest: request,
            data: data,
            requestObserved,
            answerObservation,
            currentRequestType,
            view,
            viewObserved,
            viewResponseObservation
        };

        navigate(`/establecimiento/seguimiento/${request.request.originRequestId}`, {
            state: {
                establishment,
                returnState: currentState
            }
        });
    };

    const removeFile = (indexToRemove) => {
        setResponseRequest(prevState => ({
            ...prevState,
            backups: prevState.backups.filter((_, index) => index !== indexToRemove)
        }));
    };

    const handleClosePaymentWarning = () => {
        setShowPaymentProofWarning(false);
        // Resetear el valor de sendTo
        setResponseRequest(prevState => ({
            ...prevState,
            sendTo: null
        }));
    };

    //Firebase Functions
    const uploadFile = async (file) => {
        const storageRef = ref(storage, `requests/${file.name}_${Date.now()}`);
        await uploadBytes(storageRef, file);
        return await getDownloadURL(storageRef);
    };

    const sendResponseToFirestore = async () => {
        console.log('entre a sendResponseToFirestore');
        // Verificar si se necesita mostrar el warning
        if (departmentName !== 'Departamento Tesorería' &&
            responseRequest.responseRequest?.value === 'approved' &&
            responseRequest.sendTo?.value === 'Establecimiento') {

            const hasPaymentProof = request.request.history.some(entry =>
                entry.role === 'Departamento Tesorería' &&
                entry.responseRequest === 'approved' &&
                entry.sentTo === 'Establecimiento' &&
                entry.backups?.length > 0
            );

            if (!hasPaymentProof) {
                setShowPaymentProofWarning(true);
                return; // Importante: detiene la ejecución aquí si no hay comprobante
            }
        }
        //Si pasa verificación, continua flujo normal
        const requestRef = doc(db, 'requests', request.request.id);
        const requestRefPME = doc(db, 'pme', request.request.idPME);
        const currentPME = pmeList.find(pme => pme.id === request.request.idPME);

        let uploadedFiles;

        if (responseRequest.backups && responseRequest.backups.length > 0) {
            uploadedFiles = await Promise.all(
                responseRequest.backups.map(async (file) => {
                    const downloadURL = await uploadFile(file);
                    return { 
                        name: file.name, 
                        url: downloadURL 
                    };
                })
            );
        }
        const historyArray = request.request.history;
        const lastHistoryEntry = historyArray[historyArray.length - 1];
        const penultimateEntry = historyArray[historyArray.length - 2];

        const newHistoryEntry = {
            action: `${requestObserved
                ? `Observación respondida por ${userData.permissions === 'admin' ? 'Administrador' : userData.department}`
                : responseRequest.responseRequest?.value === 'rejected'
                    ? `Solicitud rechazada por ${userData.permissions === 'admin' ? 'Administrador' : userData.department}`
                    : responseRequest.responseRequest?.value === 'observed'
                        ? `Solicitud observada por ${userData.permissions === 'admin' ? 'Administrador' : userData.department}`
                        : responseRequest.responseRequest?.value === 'approved' && responseRequest.sendTo?.value === 'Establecimiento'
                            ? `Solicitud de pago aprobada por ${userData.permissions === 'admin' ? 'Administrador' : userData.department}`
                            : `Solicitud revisada por ${userData.permissions === 'admin' ? 'Administrador' : userData.department}`
                }`,
            details: responseRequest.comments,
            responsibleName: userData.displayName,
            role: userData.permissions === 'admin' ? 'Administrador' : userData.department,
            timestamp: Timestamp.now(),
            backups: uploadedFiles || [],

            ...(requestObserved ? {} : {
                sentTo: requestObserved ? penultimateEntry.sentTo : responseRequest.sendTo.value,
                responseRequest: responseRequest.responseRequest.value,
                ...((responseRequest.responseRequest?.value === 'approved' &&
                    responseRequest.sendTo?.value === 'Establecimiento') && {
                    paymentAmount: request.request.amountToBePaid
                })
            })
        };

        try {
            await runTransaction(db, async (transaction) => {
                //Si es una observación modificamos la última entrada del historial
                if (requestObserved) {
                    const updatedHistoryEntry = {
                        ...lastHistoryEntry,
                        response: [newHistoryEntry],
                    };

                    transaction.update(requestRef, {
                        history: arrayRemove(lastHistoryEntry)
                    });
                    transaction.update(requestRef, {
                        history: arrayUnion(updatedHistoryEntry),
                        'sentTo': penultimateEntry.sentTo
                    })
                } else {
                    //Si no es una observación agregamos una nueva entrada en el historial
                    const updates = {
                        history: arrayUnion(newHistoryEntry),
                        'sentTo': responseRequest.responseRequest.value === 'rejected' ? 'Establecimiento' : responseRequest.sendTo.value,
                    }

                    if (responseRequest.responseRequest.value === 'rejected') {
                        updates.state = 'rejected'

                        // Ajustar el amountPayable según el tipo de pago
                        if (request.request.paymentType.value === 'fullPayment') {
                            // Para pago total, amountPayable debe ser igual al monto de la OC
                            updates.amountPayable = request.request.purchaseOrderAmount;
                        } else if (request.request.paymentType.value === 'partialPayment') {
                            // Para pago parcial, sumar el monto actual al amountPayable
                            updates.amountPayable = request.request.amountPayable + request.request.amountToBePaid;
                        }

                        // Actualizar la solicitud original
                        const originalRequestRef = doc(db, 'requests', request.request.originRequestId);

                        transaction.update(originalRequestRef, {
                            paymentRequest: 'inProcess'
                        });

                        // Crear notificación de rechazo
                        const notificationRef = doc(collection(db, 'notifications'));
                        const notificationData = {
                            requestId: request.request.id,
                            projectId: request.request.idProject,
                            establishmentId: request.request.idEstablishment,
                            requestType: 'payment',
                            requestStatus: 'rejected',
                            sentBy: userData.uid,
                            notificationCreated: Timestamp.now(),
                            seenBy: null,
                            viewedAt: null,
                            // Datos adicionales específicos para rechazos
                            paymentType: request.request.paymentType.value,
                            amountRejected: request.request.amountToBePaid,
                            rejectedBy: userData.displayName,
                            rejectedByRole: userData.permissions === 'admin' ? 'Administrador' : userData.department
                        };

                        transaction.set(notificationRef, notificationData);
                    }


                    if (departmentName === 'Departamento Tesorería' && responseRequest.responseRequest.value === 'approved' && responseRequest.sendTo.value === 'Establecimiento') {
                        const { paymentType, amountToBePaid, amountPayable } = request.request;

                        // Crear documento de notificación primero
                        const notificationRef = doc(collection(db, 'notifications'));
                        const notificationData = {
                            requestId: request.request.id,
                            projectId: request.request.idProject,
                            establishmentId: request.request.idEstablishment,
                            requestType: 'payment',
                            sentBy: userData.uid,
                            notificationCreated: Timestamp.now(),
                            seenBy: null,
                            viewedAt: null
                        };

                        if (paymentType === 'fullPayment' || amountPayable <= 0) {
                            updates.state = 'finished';
                            // Notificación para pago completo
                            transaction.set(notificationRef, {
                                ...notificationData,
                                requestStatus: 'finished',
                                amountPaid: Number(amountToBePaid)
                            });
                        } else {
                            updates.state = 'pending';
                            // Notificación para pago parcial
                            transaction.set(notificationRef, {
                                ...notificationData,
                                requestStatus: 'partialPayment',
                                amountPaid: Number(amountToBePaid)
                            });
                        }

                        // Actualizar la solicitud original
                        const originalRequestRef = doc(db, 'requests', request.request.originRequestId);

                        if (paymentType === 'fullPayment' || amountPayable <= 0) {
                            transaction.update(originalRequestRef, {
                                paymentRequest: 'done'
                            });
                        } else {
                            transaction.update(originalRequestRef, {
                                paymentRequest: 'inProcess'
                            });
                        }

                        //Actualización de Activities del PME si la solicitud es aprobada
                        const activityIndex = currentPME.activities.findIndex(act => act.id === request.request.idActivity);
                        if (activityIndex !== -1) {
                            const activity = currentPME.activities[activityIndex];
                            const newAccruedBalance = activity.accruedBalance + amountToBePaid;
                            const newAccruedExpense = activity.accruedExpense - amountToBePaid;
                            const newEffectiveBalance = activity.totalSubsidyAmount - newAccruedBalance;
                            const newEffectiveExpense = activity.totalSubsidyAmount - newAccruedExpense - newAccruedBalance;

                            const updatedActivities = [...currentPME.activities];
                            updatedActivities[activityIndex] = {
                                ...activity,
                                accruedBalance: newAccruedBalance,
                                accruedExpense: newAccruedExpense,
                                effectiveBalance: newEffectiveBalance,
                                effectiveExpense: newEffectiveExpense

                            };

                            transaction.update(requestRefPME, { activities: updatedActivities });
                        }
                    }
                    transaction.update(requestRef, updates);
                }
            });
            console.log('se actualizo correctamente');
            onRequestResponse(true);
        } catch (error) {
            console.error('Error updating document: ', error);
            onRequestResponse(false);
        }
    };


    //Main Action Handlers
    const handleSendResponse = async () => {
        if (!isFormValid() || isSubmitting) {
            console.log('disabled');
            return;
        }

        try {
            setIsSubmitting(true);
            await sendResponseToFirestore();
        } catch (error) {
            console.error('Error al enviar la respuesta:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    //Render Functions
    const renderRequestInfo = () => (
        <article className='info-request flex'>
            <span className='flex'>
                <p>Fecha Solicitud:</p>
                <p>{request.request.created ? formatDate(request.request?.created) : 'nulo'}</p>
            </span>
            <span className='flex'>
                <p>Establecimiento:</p>
                <p>{getEstablishment().nameEstablishment}</p>
            </span>
            <span className='flex'>
                <p>Responsable Solicitud:</p>
                <p>{formatDisplayName(request.request.responsibleName)}</p>
            </span>
        </article>
    );

    const renderRequestDetails = () => (
        <article className='container-table flex'>
            <table className='table-details-request'>
                <thead>
                    <tr>
                        <th>Dimensión</th>
                        <th>Nombre Acción</th>
                        <th>Subvención</th>
                        <th>Monto PME</th>
                        <th>Monto O.C / Contrato</th>
                        <th>Gasto Devengado</th>
                        <th>Gasto Efectivo</th>
                        <th>Saldo Devengado</th>
                        <th>Saldo Efectivo</th>
                        {/* <th>Documentos</th> */}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td data-label='Dimensión'>{formatDisplayName(request.activity?.dimension)}</td>
                        <td data-label='Nombre Acción'>{formatDisplayName(request.activity?.nameOfAction)}</td>
                        <td data-label='Subvención'>{getSubventionNames(request.activity?.subsidies)}</td>
                        <td data-label='Monto PME'>{formatCurrency(request.activity?.totalSubsidyAmount)}</td>
                        <td data-label='Monto O.C / Contrato'>{formatCurrency(request.request?.purchaseOrderAmount)}</td>
                        <td data-label='Gasto Devengado'>{formatCurrency(request.activity?.accruedExpense)}</td>
                        <td data-label='Gasto Efectivo'>{formatCurrency(request.activity?.accruedBalance)}</td>
                        <td data-label='Saldo Devengado'>{formatCurrency(request.activity?.effectiveExpense)}</td>
                        <td data-label='Saldo Efectivo'>{formatCurrency(request.activity?.effectiveBalance)}</td>
                        {/* <td>{renderBackupFiles(request.request?.backups)}</td> */}
                    </tr>
                </tbody>
            </table>
        </article>
    );

    const renderObservationDetails = () => {
        const observationDetails = getObservationDetails();
        if (!observationDetails) return null;

        return (
            <article className='observation-details flex'>
                <p className='text-bold'>Observación</p>
                <article className='observation flex'>
                    <header className='observation-header flex'>
                        <img src={info} className='icon' alt='icono de solicitud observada' />
                        <span>{observationDetails.timestamp} {observationDetails.observedBy} ({observationDetails.role}) - <strong>{observationDetails.action}</strong></span>
                    </header>
                    <section className='observation-body flex'>
                        <p><strong>Observación: </strong>{observationDetails.details}</p>
                        {observationDetails.backups.length > 0 && (
                            <span className='observation-backups flex'>
                                <p><strong>Archivos Adjuntos:</strong></p>
                                <ul className='backup-list flex'>
                                    {observationDetails.backups.map((backup, index) => (
                                        <li key={index}>
                                            <a href={backup.url} target='_blank' rel='noopener noreferrer'>{backup.name}</a>
                                        </li>
                                    ))}
                                </ul>
                            </span>
                        )}
                    </section>
                </article>
                {(observationDetails.response && observationDetails.response.length > 0) && (
                    observationDetails.response.map((response, index) => (
                        <article className='observation response flex' key={index}>
                            <header className='observation-header flex'>
                                <img src={info} className='icon' alt='icono de solicitud observada' />
                                <span>{formatDate(response.timestamp)} {formatDisplayName(response.responsibleName)} ({response.role}) - <strong>{response.action}</strong></span>
                            </header>
                            <section className='observation-body flex'>
                                <p><strong>Respuesta: </strong>{response.details}</p>
                                {response.backups.length > 0 && (
                                    <span className='observation-backups flex'>
                                        <p><strong>Archivos Adjuntos:</strong></p>
                                        <ul className='backup-list flex'>
                                            {response.backups.map((backup, index) => (
                                                <li key={index}>
                                                    <a href={backup.url} target='_blank' rel='noopener noreferrer'>{backup.name}</a>
                                                </li>
                                            ))}
                                        </ul>
                                    </span>
                                )}
                            </section>
                        </article>
                    ))
                )}
            </article>
        );
    };

    const renderResponseOptions = () => (
        <article className='response-options flex'>
            <span className='box-select'>
                <label htmlFor='responseRequest' className='label-hidden'>Respuesta de solicitud</label>
                <Select
                    name='responseRequest'
                    id='responseRequest'
                    options={staticData.requestResponses}
                    placeholder='Selecciona Respuesta'
                    className='custom-select'
                    classNamePrefix='custom-select'
                    components={{ DropdownIndicator }}
                    value={responseRequest.responseRequest}
                    onChange={handleChangeSelect}
                />
            </span>
            {(showAdditionalFields && responseRequest.responseRequest?.value !== 'rejected') && (
                <aside className='shipping-information flex'>
                    <p>Enviar a:</p>
                    <span className='box-select'>
                        <label htmlFor='sendTo' className='label-hidden'>Enviar respuesta a:</label>
                        <Select
                            name='sendTo'
                            id='sendTo'
                            options={filteredDepartments
                                // Filtrar Establecimiento si la respuesta es observed
                                .filter(department =>
                                    responseRequest.responseRequest?.value === 'observed'
                                        ? department !== 'Establecimiento'
                                        : true
                                )
                                .map((department) => ({
                                    value: department,
                                    label: department
                                }))}
                            placeholder='Selecciona Departamento'
                            className='custom-select'
                            classNamePrefix='custom-select'
                            components={{ DropdownIndicator }}
                            value={responseRequest.sendTo}
                            onChange={handleChangeSelect}
                        />
                    </span>
                </aside>
            )}
        </article>
    );

    const renderCommentsBox = () => (
        <article className={`comments-box flex ${showAdditionalFields ? 'show' : ''}`}>
            <p>{`${requestObserved ? 'Ingresar Respuesta (*)' : 'Ingresar Comentario (*)'}:`}</p>
            <textarea
                name='comments'
                placeholder={`${requestObserved ? 'Ingresa tu respuesta...' : 'Ingresa comentarios...'}`}
                value={responseRequest.comments}
                onChange={handleChange}>
            </textarea>
        </article>
    );

    const renderBackupsBox = () => {
        const isPaymentToEstablishment =
            departmentName === 'Departamento Tesorería' &&
            responseRequest.responseRequest?.value === 'approved' &&
            responseRequest.sendTo?.value === 'Establecimiento';
        return (
            <article className={`backings-box flex ${showAdditionalFields ? 'show' : ''}`}>
                <p>
                    {isPaymentToEstablishment
                        ? 'Adjuntar Comprobante de Pago (*)'
                        : 'Adjuntar Respaldos:'}
                </p>
                <aside className='upload-backups flex'>
                    <label className='button-add-backup flex' htmlFor='file-upload'>
                        <img src={upload} className='icon' alt='Icono para subir respaldo.' />
                        <p>{isPaymentToEstablishment ? 'Subir Comprobante' : 'Subir Respaldo'}</p>
                    </label>
                    <input
                        id='file-upload'
                        name='backups'
                        type='file'
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleChange}
                    />
                    {(responseRequest.backups && responseRequest.backups?.length > 0) && (
                        <ul className='file-list flex'>
                            {responseRequest.backups.map((file, index) => (
                                <li key={index} className='li-item flex' data-filename={file.name} title={file.name}>
                                    <p>{file.name}</p>
                                    <button type='button' onClick={() => removeFile(index)} aria-label={`Eliminar archivo ${file.name}`} >X</button>
                                </li>
                            ))}
                        </ul>
                    )}
                </aside>
            </article>
        );
    };

    const renderActionBox = () => (
        <article className='action-box flex'>
            <button
                type='button'
                className={`send-button flex ${showAdditionalFields ? 'show' : ''} ${(!isFormValid() || isSubmitting) ? 'disabled' : ''}`}
                disabled={!isFormValid() || isSubmitting}
                onClick={handleSendResponse}
            >
                {isSubmitting ? (
                    <p>Enviando...</p>
                ) : (
                    <>
                        <img
                            src={send}
                            className='icon'
                            alt='Icono para enviar respuesta'
                        />
                        <p>Enviar Respuesta</p>
                    </>
                )}
            </button>
            <button type='button' className='back-button flex' onClick={() => setSelectedRequest(null)} disabled={isSubmitting}>Volver</button>
        </article>
    );

    return (
        <section className='view-request payment flex'>
            <header className='header-view-request flex'>
                <p className='text-bold'>Detalles de Solicitud de Pago</p>
                <span className='header-buttons flex'>
                    <button type='button' className='request-history-button flex' onClick={handleHistoryClick}>
                        <img src={historyIcon} className='icon' alt='Icono para ver historial de solicitud' />
                        <p>Ver Historial Solicitud de Pago</p>
                    </button>
                    <button type='button' className='request-history-button flex' onClick={handleOriginalRequestHistoryClick}>
                        <img src={historyIcon} className='icon' alt='Icono para ver historial de solicitud' />
                        <p>Ver Historial Solicitud</p>
                    </button>
                </span>
            </header>
            {renderRequestInfo()}
            {renderRequestDetails()}
            <article className='payment-details flex'>
                <p>Detalle de Pago:</p>
                <article className='flex'>
                    <span className='box flex'>
                        <p>Tipo de Pago</p>
                        <p>{request.request?.paymentType.label}</p>
                    </span>
                    <span className='box flex'>
                        <p>Monto Solicitud a Pagar</p>
                        <p>{formatCurrency(request.request?.amountToBePaid)}</p>
                    </span>
                    {request.request.paymentType?.value === 'partialPayment' && (
                        <span className='box flex'>
                            <p>Monto Pendiente de Pago</p>
                            <p>{formatCurrency(request.request?.amountPayable)}</p>
                        </span>
                    )}
                </article>
            </article>
            {(answerObservation || requestObserved) && renderObservationDetails()}
            <article className='request-response-box flex'>
                <p className='text-bold'>{`${requestObserved ? 'Respuesta a Observación' : 'Respuesta Solicitud'}`}</p>
                {!requestObserved && renderResponseOptions()}
                {renderCommentsBox()}
                {renderBackupsBox()}
                <span className={`warning-popup-overlay flex ${showPaymentProofWarning ? 'show' : ''}`}>
                    <aside className={`warning-popup flex ${showPaymentProofWarning ? 'show' : ''}`}>
                        <img src={info} alt='Icono de advertencia, no existe comprobante' className='icon' />
                        <p>No se ha ingresado comprobante de pago. <br></br>Debe ser enviada al <strong>Departamento de Tesorería</strong> para su ingreso.</p>
                        <button className='button flex' type='button' onClick={handleClosePaymentWarning}><p>Aceptar</p></button>
                    </aside>
                </span>
            </article>
            {renderActionBox()}
        </section>
    )
};

const DropdownIndicator = (props) => {
    return (
        <components.DropdownIndicator {...props}>
            <img src={downArrow} alt="CustomArrow" />
        </components.DropdownIndicator>
    );
};

export default ViewPayment;